import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { NavigationEnd, RouterModule } from "@angular/router";
import { filter } from "rxjs/operators";
import { AppLoginInfo } from "alloy-performance-engine-cl";
import { BaseComponent } from "src/ancestors/base/base.component";
import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { ApeAlertComponent } from "./components/ape-alert/ape-alert.component";
import { ApeFooterComponent } from "./components/ape-footer/ape-footer.component";
import { ApeFooterService, FooterBackground } from "./components/ape-footer/ape-footer.service";
import { ApeHeaderComponent } from "./components/ape-header/ape-header.component";
import { ApeImpersonationBarComponent } from "./components/ape-impersonation-bar/ape-impersonation-bar.component";
import { ApeNotificationSidenavComponent } from "./components/ape-notification-sidenav/ape-notification-sidenav.component";
import { ApeSupportSidenavComponent } from "./components/ape-support-sidenav/ape-support-sidenav.component";
import { UserDataService } from "./services/data/user-data.service";
import { ImpersonationService } from "./services/impersonation.service";
import { LayoutService, PageBackground } from "./services/layout.service";
import { ClientAuditService } from "./services/client-audit.service";
import { NotificationStateService } from "./services/state/notification-state.service";
import { TranslateService } from "./util/translate.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ApeHeaderComponent,
    ApeFooterComponent,
    ApeAlertComponent,
    ApeImpersonationBarComponent,
    ApeNotificationSidenavComponent,
    ApeSupportSidenavComponent
  ]
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  protected fb: FormBuilder = inject(FormBuilder);

  protected translate = inject(TranslateService);

  protected envConfig: EnvironmentLoaderService = inject(EnvironmentLoaderService);

  private layoutService = inject(LayoutService);

  private footerService = inject(ApeFooterService);

  private cdr = inject(ChangeDetectorRef);

  private titleService = inject(Title);

  private userDataService = inject(UserDataService);

  private impersonationService = inject(ImpersonationService);

  private notificationState = inject(NotificationStateService);

  private clientAuditService = inject(ClientAuditService);

  constructor() {
    super();
    this.logger.setCaller("AppComponent");
  }

  protected showUserOption?: boolean;

  /** Visibilità del sidenav notifiche */
  protected notificationSidenavVisible = false;

  /** Visibilità del sidenav supporto */
  protected supportSidenavVisible = false;

  /** Indica se dobbiamo mostrare header e footer */
  protected showHeaderFooter = false;

  /** Indica se dobbiamo mostrare il footer */
  protected showFooter = false;

  /** Background corrente della pagina */
  protected currentBackground: PageBackground = "gray";

  /** Background corrente del footer */
  protected footerBackground: FooterBackground = "transparent";

  override async ngOnInit(): Promise<void> {
    // Aggiorna il titolo della pagina in base al sottodominio (solo su localhost)
    this.updatePageTitleForLocalDevelopment();

    // Carica la configurazione prima di fare qualsiasi cosa
    await this.envConfig.setAppConfig();

    // Non serve più controllare le route, il servizio gestisce tutto

    // Ascolta i cambiamenti dello stato del layout
    this.layoutService.hideHeaderFooter$.subscribe(hideHeaderFooter => {
      this.showHeaderFooter = !hideHeaderFooter;
      // Forza il change detection per evitare ExpressionChangedAfterItHasBeenCheckedError
      this.cdr.detectChanges();
    });

    // Ascolta i cambiamenti dello stato del footer
    this.layoutService.hideFooter$.subscribe(hideFooter => {
      this.showFooter = !hideFooter;
      // Forza il change detection per evitare ExpressionChangedAfterItHasBeenCheckedError
      this.cdr.detectChanges();
    });

    // Ascolta i cambiamenti del background
    this.layoutService.background$.subscribe(background => {
      this.currentBackground = background;
      // Applica il background al body per coprire tutto il viewport
      this.applyBackgroundToBody(background);
      // Imposta il background del footer automaticamente
      this.updateFooterBackground(background);
      // Forza il change detection per evitare ExpressionChangedAfterItHasBeenCheckedError
      this.cdr.detectChanges();
    });

    // Ascolta i cambiamenti del background del footer
    this.footerService.footerBackground$.subscribe(background => {
      this.footerBackground = background;
      // Forza il change detection per evitare ExpressionChangedAfterItHasBeenCheckedError
      this.cdr.detectChanges();
    });

    // Applica il background iniziale al body
    this.applyBackgroundToBody(this.currentBackground);
    // Applica il background iniziale al footer
    this.updateFooterBackground(this.currentBackground);

    // Inizializza il servizio di audit client e traccia le navigazioni di pagina
    this.clientAuditService.init();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = new URL(event.urlAfterRedirects, window.location.origin);
        const queryParams: Record<string, string> = {};
        url.searchParams.forEach((value, key) => {
          queryParams[key] = value;
        });
        this.clientAuditService.trackPageView(url.pathname, Object.keys(queryParams).length > 0
          ? queryParams
          : undefined);
      });

    const loginResposnse: AppLoginInfo = this.tStorage.retriveToken(this.tokenKey);

    /**
     * Se esiste un token ed è ancora valido schedula il rinnovo prima della scadenza
     */
    if (!this.appService.isEmpty(loginResposnse) && this.tkManager.checkTokenValidity(loginResposnse)) {
      this.tkManager.refreshTokenScheduler(loginResposnse);

      // Imposta la lingua basandosi sul JWT payload se disponibile
      if (loginResposnse.jwtPayload?.user?.langCode) {
        this.logger.info(`Initializing language from saved JWT payload: ${loginResposnse.jwtPayload.user.langCode}`);
        this.translate.initializeFromJwtPayload(loginResposnse.jwtPayload.user.langCode);
      }

      // Connetti al sistema di notifiche real-time e carica il conteggio iniziale
      this.notificationState.connectSSE();
      void this.notificationState.loadUnreadCount();
    }

    // RIMOSSO: Questo codice causava un loop di autenticazione in modalità SSO
    // perché navigava a "" (root) che fa redirect a /sso, anche quando l'utente
    // era già autenticato. Il redirect post-login è ora gestito correttamente
    // da oauth-callback.component.ts e local-authentication.component.ts
  }

  /**
   * Aggiorna il titolo della pagina se navighiamo su un sottodominio locale
   * es. franco.localhost:4200 → "franco - Performance Management | Mavriq"
   * es. localhost:4200 → "Performance Management | Mavriq" (no change)
   */
  private updatePageTitleForLocalDevelopment(): void {
    const hostname = window.location.hostname;

    // Verifica se siamo su localhost
    if (!hostname.includes("localhost")) {
      this.logger.info("Not on localhost, skipping subdomain title update");
      return;
    }

    // Estrai il sottodominio da hostname
    // Nota: window.location.hostname non include la porta
    // es. "franco.localhost" (non "franco.localhost:4200")
    const parts = hostname.split(".");

    // Se ci sono 2+ parti e l'ultima è "localhost", allora abbiamo un sottodominio
    const hasSubdomain = parts.length >= 2 && parts[parts.length - 1] === "localhost";

    if (!hasSubdomain) {
      this.logger.info("No subdomain detected on localhost");
      return;
    }

    // Estrai il sottodominio (tutte le parti prima di .localhost)
    const subdomain = parts.slice(0, -1)
      .join(".");

    if (!subdomain || subdomain.trim() === "") {
      this.logger.info("Empty subdomain detected");
      return;
    }

    // Aggiorna il titolo con il sottodominio
    const currentTitle = this.titleService.getTitle();
    const newTitle = `${subdomain} | ${currentTitle}`;
    this.titleService.setTitle(newTitle);

    this.logger.info(`Page title updated with subdomain: ${subdomain}`);
  }

  /**
   * Applica il background al body per coprire tutto il viewport
   * @param background - Il tipo di background da applicare
   */
  private applyBackgroundToBody(background: PageBackground): void {
    const body = document.body;
    if (background === "gray") {
      body.style.backgroundColor = "var(--colors-gray-scale-light-gray)";
    } else {
      body.style.backgroundColor = "var(--colors-gray-scale-white)";
    }
  }

  /**
   * Aggiorna il background del footer basandosi sul background della pagina
   * @param background - Il tipo di background della pagina
   */
  private updateFooterBackground(background: PageBackground): void {
    if (background === "gray") {
      // Quando la pagina è grigia, il footer deve essere bianco per contrastare
      this.footerService.setFooterBackground("white");
    } else {
      // Quando la pagina è bianca, il footer può essere trasparente
      this.footerService.setFooterBackground("transparent");
    }
  }

  /**
   * Termina l'impersonificazione e reindirizza alla pagina admin
   */
  protected async endImpersonation(): Promise<void> {
    await this.impersonationService.endImpersonation();
  }

  override async ngOnDestroy(): Promise<void> {
    await super.ngOnDestroy();
    // Disconnetti dal sistema di notifiche real-time
    this.notificationState.disconnectSSE();
  }
}
